import { QueryKeys } from "@/constants/query-keys";
import { useSignUp } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { useRegisterFinalization } from "./use-register-finalization";

type RegisterPayload = {
  email: string;
  password: string;
};
type VerifyEmailOtpPayload = {
  code: string;
  email: string;
};

type ClerkErrorIssue = {
  longMessage?: string;
  message?: string;
};

type ClerkLikeError = {
  errors?: ClerkErrorIssue[];
};

const REGISTER_PENDING_CREDENTIALS_KEY = "register_pending_credentials";

type PendingRegisterCredentials = {
  email: string;
  password: string;
};

const getClerkErrorMessage = (error: unknown) => {
  const clerkLikeError = error as ClerkLikeError | undefined;
  const firstIssue = clerkLikeError?.errors?.[0];
  return firstIssue?.longMessage || firstIssue?.message;
};

const toError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error && error.message) {
    return error;
  }

  const clerkMessage = getClerkErrorMessage(error);
  if (clerkMessage) {
    return new Error(clerkMessage);
  }

  return new Error(fallbackMessage);
};

const savePendingCredentials = ({ email, password }: PendingRegisterCredentials) => {
  sessionStorage.setItem(
    REGISTER_PENDING_CREDENTIALS_KEY,
    JSON.stringify({
      email,
      password,
    }),
  );
};

const getPendingCredentials = (): PendingRegisterCredentials | null => {
  const rawValue = sessionStorage.getItem(REGISTER_PENDING_CREDENTIALS_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as PendingRegisterCredentials;
    if (!parsed.email || !parsed.password) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const clearPendingCredentials = () => {
  sessionStorage.removeItem(REGISTER_PENDING_CREDENTIALS_KEY);
};

export const useRegister = () => {
  const { isLoaded, signUp, setActive } = useSignUp();
  const { finalizeRegistration } = useRegisterFinalization();

  const ensureSignUpReady = () => {
    if (!isLoaded || !signUp) {
      throw new Error("Sign-up is not ready yet. Please try again.");
    }
  };

  const registerMutation = useMutation({
    mutationKey: [QueryKeys.REGISTER],
    mutationFn: async ({ email, password }: RegisterPayload): Promise<void> => {
      if (!signUp) {
        throw new Error("Sign-up is not ready yet. Please try again.");
      }
      try {
        ensureSignUpReady();

        const signUpResult = await signUp.create({
          emailAddress: email,
        });

        if (signUpResult.status === "complete") {
          throw new Error(
            "Unable to start OTP verification. In Clerk, ensure Email is a sign-up identifier and Password is enabled.",
          );
        }

        try {
          await signUpResult.prepareEmailAddressVerification({
            strategy: "email_code",
          });
          savePendingCredentials({ email, password });
          return;
        } catch (error) {
          const clerkMessage = getClerkErrorMessage(error);
          const noSignUpAttemptError = clerkMessage?.includes("No sign up attempt was found");

          if (noSignUpAttemptError || signUpResult.status === "complete") {
            throw new Error(
              "Clerk could not start email OTP verification. In Clerk, ensure Email and Password sign-up are enabled.",
            );
          }

          throw error;
        }
      } catch (error) {
        clearPendingCredentials();
        throw toError(error, "Failed to register.");
      }
    },
  });

  const verifyEmailOtpMutation = useMutation({
    mutationKey: [QueryKeys.REGISTER, "verify-email-otp"],
    mutationFn: async ({ code, email }: VerifyEmailOtpPayload) => {
      try {
        if (!signUp) {
          throw new Error("Sign-up is not ready yet. Please try again.");
        }

        ensureSignUpReady();

        const verificationResult = await signUp.attemptEmailAddressVerification({
          code,
        });

        let completedSignUp = verificationResult;

        if (verificationResult.status !== "complete") {
          const pendingCredentials = getPendingCredentials();
          if (!pendingCredentials) {
            throw new Error("Registration session expired. Please sign up again.");
          }

          const isSameEmail = pendingCredentials.email.toLowerCase() === email.toLowerCase();
          if (!isSameEmail) {
            throw new Error("Registration email does not match. Please sign up again.");
          }

          completedSignUp = await signUp.update({
            password: pendingCredentials.password,
          });
        }

        if (completedSignUp.status !== "complete" || !completedSignUp.createdSessionId) {
          throw new Error("Email verification failed. Please try again.");
        }

        await setActive({ session: completedSignUp.createdSessionId });
        await finalizeRegistration(email);
        clearPendingCredentials();
      } catch (error) {
        throw toError(error, "Failed to verify email code.");
      }
    },
  });

  const resendEmailOtpMutation = useMutation({
    mutationKey: [QueryKeys.REGISTER, "resend-email-otp"],
    mutationFn: async () => {
      try {
        if (!signUp) {
          throw new Error("Sign-up is not ready yet. Please try again.");
        }

        ensureSignUpReady();
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });
      } catch (error) {
        throw toError(error, "Failed to resend verification code.");
      }
    },
  });

  return {
    isLoading: registerMutation.isPending,
    isError: registerMutation.isError,
    error: registerMutation.error,
    register: registerMutation.mutate,
    registerAsync: registerMutation.mutateAsync,
    isVerifyingEmailOtp: verifyEmailOtpMutation.isPending,
    verifyEmailOtpError: verifyEmailOtpMutation.error,
    isVerifyEmailOtpError: verifyEmailOtpMutation.isError,
    verifyEmailOtp: verifyEmailOtpMutation.mutate,
    verifyEmailOtpAsync: verifyEmailOtpMutation.mutateAsync,
    isResendingEmailOtp: resendEmailOtpMutation.isPending,
    resendEmailOtpError: resendEmailOtpMutation.error,
    isResendEmailOtpError: resendEmailOtpMutation.isError,
    resendEmailOtp: resendEmailOtpMutation.mutate,
    resendEmailOtpAsync: resendEmailOtpMutation.mutateAsync,
  };
};
